import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { searchExercises, type ExerciseSearchResult } from "@/integrations/backend/workout-generator";
import { openAdminExerciseSearch } from "./utils";

type Props = {
  placeholder?: string;
  mappedExerciseIds: string[];
  onAddExercise: (exercise: ExerciseSearchResult) => void;
  disabled?: boolean;
};

export default function ExerciseSearchCombobox({
  placeholder = "Search exercises...",
  mappedExerciseIds,
  onAddExercise,
  disabled,
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ExerciseSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const mappedSet = useMemo(() => new Set(mappedExerciseIds), [mappedExerciseIds]);

  useEffect(() => {
    if (disabled) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setLoading(true);
      void searchExercises(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, disabled]);

  if (disabled) return null;

  return (
    <div className="space-y-1">
      <Input
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="h-8 border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-500 focus-visible:ring-gray-400"
      />
      {loading ? <p className="text-xs text-gray-500">Searching…</p> : null}
      {results.length > 0 ? (
        <ul className="max-h-32 overflow-y-auto rounded border border-gray-200 bg-white text-sm shadow-sm">
          {results.map((ex) => {
            const alreadyMapped = mappedSet.has(ex.id);
            return (
              <li key={ex.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-gray-900 hover:bg-gray-50"
                  onClick={() => {
                    if (alreadyMapped) {
                      openAdminExerciseSearch(ex.name);
                      setResults([]);
                      return;
                    }
                    onAddExercise(ex);
                    setQuery("");
                    setResults([]);
                  }}
                >
                  <span>{ex.name}</span>
                  {alreadyMapped ? (
                    <span className="shrink-0 text-xs text-gray-500">View in catalog</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
